// apps/dashboard/src/components/NpsSurveyModal.tsx
// In-app NPS survey modal — appears once the trigger job schedules a survey.
// Positioned as a floating card in the bottom-right corner (non-blocking).
// After respond or dismiss, modal disappears for 90 days (server-side suppression).
// SPEC: docs/specs/tenant/sprint-am-nps.spec.md

import { useNpsDismiss, useNpsRespond, useNpsSurvey } from '@/hooks/use-nps';
import { cn } from '@/lib/cn';
import { X } from 'lucide-react';
import { useState } from 'react';

const SCORES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

export function NpsSurveyModal() {
  const { data } = useNpsSurvey();
  const respond = useNpsRespond();
  const dismiss = useNpsDismiss();

  const [selected, setSelected] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const survey = data?.survey;
  if (!survey) return null;

  if (submitted) {
    return (
      <div className="fixed bottom-6 right-6 z-50 w-80 rounded-2xl border border-border bg-card shadow-xl p-5">
        <div className="flex flex-col items-center gap-2 py-2">
          <span className="text-2xl">🎉</span>
          <p className="font-semibold text-sm text-center">Obrigado pelo seu feedback!</p>
          <p className="text-xs text-muted-foreground text-center">
            Sua resposta nos ajuda a melhorar o OmniFlow.
          </p>
        </div>
      </div>
    );
  }

  const handleRespond = () => {
    if (selected === null) return;
    respond.mutate(
      {
        surveyId: survey.id,
        score: selected,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      },
      { onSuccess: () => setSubmitted(true) },
    );
  };

  const handleDismiss = () => {
    dismiss.mutate({ surveyId: survey.id });
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 rounded-2xl border border-border bg-card shadow-xl p-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold leading-snug">
          De 0 a 10, qual a probabilidade de recomendar o OmniFlow a um colega?
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismiss.isPending}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Score buttons */}
      <div className="space-y-1.5">
        <div className="grid grid-cols-11 gap-1">
          {SCORES.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setSelected(n)}
              className={cn(
                'h-7 w-full rounded text-xs font-medium transition-colors',
                selected === n
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {n}
            </button>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>Muito improvável</span>
          <span>Extremamente provável</span>
        </div>
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={500}
        rows={2}
        placeholder="O que motivou sua nota? (opcional)"
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-xs resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      />

      {/* Actions */}
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismiss.isPending}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Agora não
        </button>
        <button
          type="button"
          onClick={handleRespond}
          disabled={selected === null || respond.isPending}
          className={cn(
            'rounded-lg px-4 py-1.5 text-xs font-medium transition-colors',
            selected !== null
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-muted text-muted-foreground cursor-not-allowed',
          )}
        >
          {respond.isPending ? 'Enviando…' : 'Enviar'}
        </button>
      </div>
    </div>
  );
}
